import React, { useEffect, useState } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    CardContent,
    Checkbox,
    Grid,
    TableCell,
    TextField,
    Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import styled from '@emotion/styled';
import { CustomTable } from '../../components/CustomTable';
import {
    GetMenusWithOverRideSchoolControl,
    getAllSchools,
    InsertOrUpdateMenuSchoolControl
} from '../../api/auth/authService';
import { getSchoolByID } from '../../api/SchoolService/SchoolService';

import { createFilterOptions } from '@mui/material/Autocomplete';

export default function AdminWebMenu() {

    const [adminCheckedMap, setAdminCheckedMap] = useState({});
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState("1000");
    const [schoolProfile, setSchoolProfile] = useState(null);

    const schoolProfileData = schoolProfile || {};

    const [flatAdminRows, setFlatAdminRows] = useState([]);
    const [adminParentMap, setAdminParentMap] = useState({});

    const [adminRoleId, setAdminRoleId] = useState("");
    const [adminMenuDetails, setAdminMenuDetails] = useState({});

    const roles = [{ value: 'Admin' }];
    const tableHeaders = ['Menus', 'Status'];

    const [initialAdminCheckedMap, setInitialAdminCheckedMap] = useState({});
    const [adminSortOrderMap, setAdminSortOrderMap] = useState({});
    const [initialAdminSortOrderMap, setInitialAdminSortOrderMap] = useState({});

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        fontSize: '0.85rem',
        whiteSpace: 'nowrap',
        borderBottom: '1px solid #efefef',
    }));

    const fetchSchools = async () => {
        try {
            const response = await getAllSchools(page, rowsPerPage);

            if (response.success && response.data?.schools) {
                const fetchedSchools = response.data.schools.map(school => ({
                    id: school.schoolID,
                    schoolCode: school.schoolCode || "",
                    SchoolName: school.schoolName || "",
                    BranchName: school.schoolBranchName || "",
                    Address: school.address,
                    City: school.city,
                    State: school.state,
                }));

                setSchools(fetchedSchools);
            } else {
                setSchools([]);
            }
        } catch (error) {
            setSchools([]);
        }
    };

    useEffect(() => {
        fetchSchools();
    }, [page, rowsPerPage]);

    const fetchSchoolProfile = async () => {
        if (!selectedSchool?.id) {
            setSchoolProfile(null);
            return;
        }
        try {
            const response = await getSchoolByID(selectedSchool.id);
            if (response?.success && response?.data) {
                setSchoolProfile(response.data);
            } else {
                console.warn("Failed to fetch school profile", "warning");
                setSchoolProfile(null);
            }
        } catch (error) {
            console.error("Error fetching school profile", error);
            setSchoolProfile(null);
        }
    };

    const fetchSchoolMenus = async () => {
        if (!selectedSchool?.id) {
            setFlatAdminRows([]);
            setAdminParentMap({});
            setAdminCheckedMap({});
            setAdminSortOrderMap({});
            return;
        }

        try {
            const formData = new FormData();
            formData.append("SchoolId", selectedSchool.id);
            formData.append("RoleID", "");
            formData.append("DesignationID", "");

            const response = await GetMenusWithOverRideSchoolControl(formData);

            if (response?.data) {
                const adminRoleData = response.data.find(r => r.roleName === 'Admin');
                setAdminRoleId(adminRoleData?.roleId || "");
                const menuData = adminRoleData?.menuList || [];

                const newFlatRows = [];
                const newParentMap = {};
                const newCheckedMap = {};
                const newMenuDetails = {};
                const newSortOrderMap = {};

                menuData.forEach(menu => {
                    const parentId = String(menu.menuID);
                    const parentLabel = menu.subMenuName;
                    const isAssigned = menu.isAssign === true;

                    newMenuDetails[parentId] = {
                        canView: menu.canView,
                        canCreate: menu.canCreate,
                        canEdit: menu.canEdit,
                        canDelete: menu.canDelete
                    };

                    newFlatRows.push({
                        id: parentId,
                        label: parentLabel,
                        type: 'group',
                        group: null
                    });

                    newCheckedMap[parentId] = {
                        isActive: menu.isActive ?? isAssigned,
                        isMenuShow: menu.isMenuShow ?? isAssigned
                    };

                    newSortOrderMap[parentId] = menu.sortOrder ?? '';

                    if (menu.subMenus && Array.isArray(menu.subMenus)) {
                        menu.subMenus.forEach(subMenu => {
                            const childId = String(subMenu.menuID);
                            const childLabel = subMenu.subMenuName;
                            const isChildAssigned = subMenu.isAssign || false;

                            newMenuDetails[childId] = {
                                canView: subMenu.canView,
                                canCreate: subMenu.canCreate,
                                canEdit: subMenu.canEdit,
                                canDelete: subMenu.canDelete
                            };

                            newFlatRows.push({
                                id: childId,
                                label: childLabel,
                                type: 'item',
                                group: parentLabel
                            });

                            newParentMap[childId] = parentId;

                            newCheckedMap[childId] = {
                                isActive: subMenu.isActive ?? isChildAssigned,
                                isMenuShow: subMenu.isMenuShow ?? isChildAssigned
                            };

                            newSortOrderMap[childId] = subMenu.sortOrder ?? '';
                        });
                    }
                });

                setFlatAdminRows(newFlatRows);
                setAdminParentMap(newParentMap);
                setAdminCheckedMap(newCheckedMap);
                setAdminMenuDetails(newMenuDetails);
                setInitialAdminCheckedMap(JSON.parse(JSON.stringify(newCheckedMap)));
                setAdminSortOrderMap(newSortOrderMap);
                setInitialAdminSortOrderMap(JSON.parse(JSON.stringify(newSortOrderMap)));
            }
        } catch (error) {
            console.error("Error fetching menus", error);
        }
    };



    useEffect(() => {
        fetchSchoolProfile();
        fetchSchoolMenus();
    }, [selectedSchool]);

    const handleSubmit = async () => {
        if (!selectedSchool?.id || !adminRoleId) {
            console.warn("School or Role ID missing");
            return;
        }

        const roleMenuMappingList = [];
        flatAdminRows.forEach(row => {
            const menuID = row.id;
            const details = adminMenuDetails[menuID] || { canView: true, canCreate: true, canEdit: true, canDelete: true };
            const isActiveChecked = Boolean(adminCheckedMap?.[menuID]?.isActive);
            const isMenuShowChecked = Boolean(adminCheckedMap?.[menuID]?.isMenuShow);
            const wasActiveChecked = Boolean(initialAdminCheckedMap?.[menuID]?.isActive);
            const wasMenuShowChecked = Boolean(initialAdminCheckedMap?.[menuID]?.isMenuShow);
            const sortOrderRaw = adminSortOrderMap?.[menuID];
            const sortOrder = sortOrderRaw === '' || sortOrderRaw === undefined || sortOrderRaw === null
                ? null
                : parseInt(sortOrderRaw, 10);
            const wasSortOrderRaw = initialAdminSortOrderMap?.[menuID];
            const wasSortOrder = wasSortOrderRaw === '' || wasSortOrderRaw === undefined || wasSortOrderRaw === null
                ? null
                : parseInt(wasSortOrderRaw, 10);

        if (isActiveChecked !== wasActiveChecked || isMenuShowChecked !== wasMenuShowChecked || sortOrder !== wasSortOrder) {
            roleMenuMappingList.push({
                roleID: adminRoleId,
                designationID: null,
                menuID: menuID,
                isActiveChecked: isActiveChecked,
                isMenuShowChecked: isMenuShowChecked,
                sortOrder: sortOrder,
                canView: details.canView,
                canCreate: details.canCreate,
                canEdit: details.canEdit,
                canDelete: details.canDelete
            });
        }
        });
    if (roleMenuMappingList.length === 0) {
        alert("No changes detected.");
        return;
    }

        const loggedInUserId = "094775c0-c596-4c45-b7c0-223851d1daff";

        const payload = {
            schoolID: selectedSchool.id,
            roleMenuMappingList: roleMenuMappingList,
            userID: loggedInUserId
        };

        try {
            const response = await InsertOrUpdateMenuSchoolControl(payload);
            if (response?.success) {
                alert("Menus updated successfully!");
                setInitialAdminCheckedMap(JSON.parse(JSON.stringify(adminCheckedMap)));
                setInitialAdminSortOrderMap(JSON.parse(JSON.stringify(adminSortOrderMap)));
            } else {
                alert("Failed to update menus.");
            }
        } catch (error) {
            console.error("Error updating menus", error);
            alert("Error updating menus.");
        }
    };

    const filterOptions = createFilterOptions({
        matchFrom: 'any',
        stringify: (option) => `${option.SchoolName || ""} ${option.BranchName || ""}`,
    });

    return (
        <>
            <Box
                sx={{
                    background: "#fff",
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #f5f9ff, #eef3ff)",
                        border: "1px solid #e0e7ff",
                        gap: 2,
                        p: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#1e3a8a", lineHeight: 1.2 }}>
                            Admin Web Menus
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                            Track and manage all school leads
                        </Typography>
                    </Box>

                    <Box>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box display="flex" gap={2} sx={{ alignItems: "center" }}>
                                <Box width={700}>
                                    <Autocomplete
                                        size="small"
                                        options={schools}
                                        filterOptions={filterOptions}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        getOptionLabel={(option) => {
                                            if (!option) return "";
                                            if (typeof option === "string") return option;
                                            const branchString = option.BranchName ? ` - ${option.BranchName}` : "";
                                            return `${option.SchoolName || ""}${branchString}`;
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                <div>
                                                    <strong>{option.SchoolName}</strong>
                                                    {option.BranchName && <b> - {option.BranchName}</b>}
                                                </div>
                                            </li>
                                        )}
                                        value={selectedSchool}
                                        onChange={(event, newValue) => {
                                            setSelectedSchool(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="School & Branch Name"
                                                variant="outlined"
                                                sx={{ bgcolor: "#fff" }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        </LocalizationProvider>
                    </Box>
                </Box>
            </Box>


            {schoolProfile && (
                <Grid container spacing={2} mt={2}>
                    {/* School Detail Section */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <CardContent
                            sx={{
                                background: "#fff",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Box display={'flex'} flexWrap={{ xs: 'wrap', sm: 'inherit' }} gap={2}>
                                <Grid container rowSpacing={2} columnSpacing={2}>

                                    {/* School Name */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                School Name :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.schoolName || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Branch Name */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                Branch Name :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.schoolBranchName || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* School Code */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                School Code :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.schoolCode || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* State */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                State :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.state || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* City */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                City :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.city || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Pin Code */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                Pin Code :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.pinCode || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Address */}
                                    <Grid item size={{ xs: 12 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                Address :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.address || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                </Grid>
                            </Box>
                        </CardContent>
                    </Grid>

                    {/* Menu Status Section */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <CardContent
                            // paddingBottom="16px !important"
                            sx={{
                                background: "#fff",
                                borderRadius: 2,
                            }}
                        >
                            <Box sx={{ border: '1px solid #e0e7ff', mb: 2, borderRadius: '8px', overflow: 'hidden' }}>
                                <CustomTable
                                    headers={tableHeaders}
                                    rows={flatAdminRows}
                                    stickyHeader="40vh"
                                    renderRow={(row) => {
                                        const isParentRow = row.type === "group" || row.group === null;

                                        if (isParentRow) {
                                            return (
                                                <>
                                                    <StyledTableCell sx={{ fontWeight: 600, background: '#eef3ff' }}>
                                                        {row.label}
                                                    </StyledTableCell>

                                                    {roles.map(role => {
                                                        return (
                                                            <StyledTableCell key={role.value} sx={{ padding: "4px 16px", background: '#eef3ff', borderLeft: '1px solid #ccc' }}>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <Box display="flex" flexDirection="column" alignItems="center">
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={Boolean(adminCheckedMap?.[row.id]?.isActive)}
                                                                            indeterminate={(() => {
                                                                                const childItems = flatAdminRows.filter(r => r.group === row.label && r.type === "item");
                                                                                if (childItems.length === 0) return false;

                                                                                const checkedCount = childItems.filter(item =>
                                                                                    adminCheckedMap?.[item.id]?.isActive === true
                                                                                ).length;

                                                                                return checkedCount > 0 && checkedCount < childItems.length;
                                                                            })()}
                                                                            onChange={() => {
                                                                                const childItems = flatAdminRows.filter(r => r.group === row.label && r.type === "item");

                                                                                setAdminCheckedMap(prev => {
                                                                                    const updated = { ...prev };
                                                                                    const current = prev?.[row.id]?.isActive || false;
                                                                                    const newValue = !current;

                                                                                    updated[row.id] = { ...updated[row.id], isActive: newValue };

                                                                                    childItems.forEach(item => {
                                                                                        updated[item.id] = { ...updated[item.id], isActive: newValue };
                                                                                    });

                                                                                    return updated;
                                                                                });
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>Active</Typography>
                                                                    </Box>
                                                                    <Box display="flex" flexDirection="column" alignItems="center">
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={Boolean(adminCheckedMap?.[row.id]?.isMenuShow)}
                                                                            indeterminate={(() => {
                                                                                const childItems = flatAdminRows.filter(r => r.group === row.label && r.type === "item");
                                                                                if (childItems.length === 0) return false;

                                                                                const checkedCount = childItems.filter(item =>
                                                                                    adminCheckedMap?.[item.id]?.isMenuShow === true
                                                                                ).length;

                                                                                return checkedCount > 0 && checkedCount < childItems.length;
                                                                            })()}
                                                                            onChange={() => {
                                                                                const childItems = flatAdminRows.filter(r => r.group === row.label && r.type === "item");

                                                                                setAdminCheckedMap(prev => {
                                                                                    const updated = { ...prev };
                                                                                    const current = prev?.[row.id]?.isMenuShow || false;
                                                                                    const newValue = !current;

                                                                                    updated[row.id] = { ...updated[row.id], isMenuShow: newValue };

                                                                                    childItems.forEach(item => {
                                                                                        updated[item.id] = { ...updated[item.id], isMenuShow: newValue };
                                                                                    });

                                                                                    return updated;
                                                                                });
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>Show</Typography>
                                                                    </Box>
                                                                    <TextField
                                                                        size="small"
                                                                        type="number"
                                                                        variant="outlined"
                                                                        label="Sort"
                                                                        value={adminSortOrderMap?.[row.id] ?? ''}
                                                                        onChange={(e) => {
                                                                            const value = e.target.value;
                                                                            setAdminSortOrderMap(prev => ({
                                                                                ...prev,
                                                                                [row.id]: value
                                                                            }));
                                                                        }}
                                                                        sx={{ width: 80, bgcolor: '#fff' }}
                                                                        inputProps={{ style: { padding: '4px 8px', fontSize: '0.75rem' } }}
                                                                    />
                                                                </Box>
                                                            </StyledTableCell>
                                                        )
                                                    })}
                                                </>
                                            );
                                        }

                                        return (
                                            <>
                                                <StyledTableCell sx={{ padding: "4px 16px", paddingLeft: "32px", }}>
                                                    {row.label}
                                                </StyledTableCell>

                                                {roles.map(role => {
                                                    const menuID = row.id;

                                                    return (
                                                        <StyledTableCell key={role.value} sx={{ padding: "4px 16px", borderLeft: '1px solid #ccc' }}>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box display="flex" flexDirection="column" alignItems="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={Boolean(adminCheckedMap?.[menuID]?.isActive)}
                                                                        onChange={() => {
                                                                            setAdminCheckedMap(prev => {
                                                                                const updated = { ...prev };
                                                                                const current = prev?.[menuID]?.isActive || false;
                                                                                const newValue = !current;

                                                                                updated[menuID] = { ...updated[menuID], isActive: newValue };

                                                                                const parentId = adminParentMap[menuID];
                                                                                if (parentId) {
                                                                                    const siblings = Object.keys(adminParentMap).filter(key => adminParentMap[key] === parentId);
                                                                                    const anyChildChecked = siblings.some(sibId => updated?.[sibId]?.isActive === true);
                                                                                    updated[parentId] = { ...updated[parentId], isActive: anyChildChecked };
                                                                                }

                                                                                return updated;
                                                                            });
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>Active</Typography>
                                                                </Box>
                                                                <Box display="flex" flexDirection="column" alignItems="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={Boolean(adminCheckedMap?.[menuID]?.isMenuShow)}
                                                                        onChange={() => {
                                                                            setAdminCheckedMap(prev => {
                                                                                const updated = { ...prev };
                                                                                const current = prev?.[menuID]?.isMenuShow || false;
                                                                                const newValue = !current;

                                                                                updated[menuID] = { ...updated[menuID], isMenuShow: newValue };

                                                                                const parentId = adminParentMap[menuID];
                                                                                if (parentId) {
                                                                                    const siblings = Object.keys(adminParentMap).filter(key => adminParentMap[key] === parentId);
                                                                                    const anyChildChecked = siblings.some(sibId => updated?.[sibId]?.isMenuShow === true);
                                                                                    updated[parentId] = { ...updated[parentId], isMenuShow: anyChildChecked };
                                                                                }

                                                                                return updated;
                                                                            });
                                                                        }}
                                                                    />
                                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#64748b' }}>Show</Typography>
                                                                </Box>
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    variant="outlined"
                                                                    label="Sort"
                                                                    value={adminSortOrderMap?.[menuID] ?? ''}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setAdminSortOrderMap(prev => ({
                                                                            ...prev,
                                                                            [menuID]: value
                                                                        }));
                                                                    }}
                                                                    sx={{ width: 80, bgcolor: '#fff' }}
                                                                    inputProps={{ style: { padding: '4px 8px', fontSize: '0.75rem' } }}
                                                                />
                                                            </Box>
                                                        </StyledTableCell>
                                                    )
                                                })}
                                            </>
                                        );
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    onClick={handleSubmit}
                                    variant="contained"
                                    sx={{
                                        boxShadow: "none",
                                        whiteSpace: "nowrap",
                                        "&:hover": { boxShadow: "none" },
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </CardContent>
                    </Grid>
                </Grid>
            )}
        </>
    )
}